import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import usePlaceIdLookup, { LatLngLiteral } from "../hooks/usePlaceIdLookup";
import { IStations } from "../interfaces/endpoints/IStations";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "14px"
};

//kosovo
const center = { lat: 42.65234783876951, lng: 21.1579763948839 };

type coordName = {
  lat: any;
  lng: any;
};

export interface IGmap {
  className?: string;
  coordinates?: LatLngLiteral;
  names: coordName;
  placeId?: string;
}

const lookupErrorMessages: Record<string, string> = {
  invalid: "Place ID i pavlefshëm.",
  notFound: "Nuk u gjet asnjë lokacion për këtë Place ID.",
  quotaExceeded: "Limiti mujor i kërkimeve të Place ID u arrit.",
  denied: "Kërkimi u bllokua: qasje e refuzuar në Places API.",
  error: "Kërkimi dështoi. Provo përsëri."
};

const GmapLoaded: React.FC<IGmap & { apiKey: string }> = (props) => {
  const { coordinates, names, placeId, apiKey } = props;
  const [markers, setMarkers] = useState<LatLngLiteral>();
  const [position, setPosition] = useState<LatLngLiteral>(center);
  const [zoom, setZoom] = useState<number>(9);
  const [clicked, setClicked] = useState<boolean>(false);
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey
  });
  const placeLookup = usePlaceIdLookup(placeId, apiKey);

  const {
    setValue,
    formState: { errors }
  } = useFormContext<IStations>();

  useEffect(() => {
    if (isLoaded && coordinates && !clicked) {
      // Records without coordinates come through as (0,0) — keep the Kosovo
      // default instead of centering on the ocean.
      if (!coordinates.lat && !coordinates.lng) return;
      setPosition(coordinates);
      setMarkers({ lat: coordinates.lat, lng: coordinates.lng });
      setZoom(13);
    }
  }, [coordinates]);

  useEffect(() => {
    if (placeLookup.status === "found" && placeLookup.coords) {
      setMarkers(placeLookup.coords);
      setPosition(placeLookup.coords);
      setZoom(15);
      setValue(names.lat, placeLookup.coords.lat, { shouldDirty: true });
      setValue(names.lng, placeLookup.coords.lng, { shouldDirty: true });
      setClicked(true);
    } else if (lookupErrorMessages[placeLookup.status]) {
      toast.error(lookupErrorMessages[placeLookup.status], {
        id: "placeIdLookup",
        position: "top-right"
      });
    }
  }, [placeLookup]);

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const newMarker = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      setMarkers(newMarker);
      // Dirty, so the unsaved-changes prompt and the GTFS follow check notice a moved pin.
      setValue(names.lat, newMarker.lat, { shouldDirty: true });
      setValue(names.lng, newMarker.lng, { shouldDirty: true });
      setClicked(true);
    }
  };

  return isLoaded ? (
    <div>
      <GoogleMap
        clickableIcons={true}
        mapContainerStyle={mapContainerStyle}
        center={position}
        zoom={zoom}
        onClick={handleMapClick}
      >
        {markers ? (
          <Marker
            draggable
            onDragEnd={handleMapClick}
            animation={google.maps.Animation.DROP}
            position={{ lat: markers.lat, lng: markers.lng }}
          />
        ) : null}
      </GoogleMap>
      {markers ? (
        <div className="flex flex-row justify-end mt-2">
          <p className="text-xs text-primary mr-1">Koordinatat:</p>
          <p className="text-xs text-gray-700">{markers.lat},</p>
          <p className="text-xs text-gray-700 ml-1"> {markers.lng}</p>
        </div>
      ) : null}
      <p className="text-red-500 text-xs mt-0.5">{errors.station?.latitude?.message}</p>
    </div>
  ) : (
    <></>
  );
};

export default GmapLoaded;
